import React from 'react';
import {
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    PanelType,
    PrimaryButton,
    DefaultButton,
    Stack,
    Text,
    TextField
} from '@fluentui/react';
import {toast} from 'react-toastify';
import PanelWrapper from '../Common/PanelWrapper';
import RenderIf from '../Common/RenderIf';
import {ArrayBufferToBase64} from '../../logic/ArrayBufferConverter';
import {
    CsvImportType,
    ICsvImportResult,
    useRunCsvImportMutation,
    useUploadCsvImportFileMutation
} from '../../store/endpoints/CsvImportEndpoints';

interface ICsvImportPanelProps {
    importType: CsvImportType;
    isOpen: boolean;
    onClose: () => void;
    /** Called after a run so the caller can refresh its grid (even a partial run may have imported rows). */
    onImported?: () => void;
}

interface IImportField {
    key: string;
    label: string;
    required?: boolean;
    /** Normalized header names (lowercase, alphanumeric only) that auto-map to this field. */
    aliases: string[];
}

// Field keys must match CsvImportService.Fields on the API.
const ADDRESS_FIELDS: IImportField[] = [
    {key: 'address1', label: 'Address 1', aliases: ['address1', 'address', 'addressline1', 'street']},
    {key: 'address2', label: 'Address 2', aliases: ['address2', 'addressline2']},
    {key: 'address3', label: 'Address 3', aliases: ['address3', 'addressline3']},
    {key: 'city', label: 'City', aliases: ['city', 'town']},
    {key: 'stateTerritory', label: 'State/Territory', aliases: ['stateterritory', 'state', 'province', 'territory']},
    {key: 'postalCodeZip', label: 'Postal Code/Zip', aliases: ['postalcodezip', 'postalcode', 'zip', 'zipcode']},
    {key: 'country', label: 'Country', aliases: ['country']},
];

const IMPORT_FIELDS: {[type in CsvImportType]: IImportField[]} = {
    company: [
        {key: 'referenceId', label: 'ID', required: true, aliases: ['id', 'companyid', 'recid', 'externalid']},
        {key: 'name', label: 'Company Name', required: true, aliases: ['companyname', 'name', 'company']},
        {key: 'identifier', label: 'Company Identifier', aliases: ['companyidentifier', 'identifier']},
        ...ADDRESS_FIELDS,
        {key: 'website', label: 'Website', aliases: ['website', 'url']},
        {key: 'phone', label: 'Phone', aliases: ['phone', 'phonenumber', 'telephone']},
    ],
    contact: [
        {key: 'companyReferenceId', label: 'Company ID', required: true, aliases: ['companyid', 'company', 'companyname']},
        {key: 'email', label: 'Email Address', required: true, aliases: ['email', 'emailaddress']},
        {key: 'referenceId', label: 'ID', aliases: ['id', 'contactid', 'recid', 'externalid']},
        {key: 'firstName', label: 'First Name', aliases: ['firstname', 'first']},
        {key: 'lastName', label: 'Last Name', aliases: ['lastname', 'last', 'surname']},
        {key: 'phone', label: 'Phone', aliases: ['phone', 'phonenumber', 'telephone']},
    ],
    location: [
        {key: 'companyReferenceId', label: 'Company ID', required: true, aliases: ['companyid', 'company', 'companyname']},
        {key: 'referenceId', label: 'ID', aliases: ['id', 'locationid', 'siteid', 'recid', 'externalid']},
        {key: 'name', label: 'Location Name', aliases: ['locationname', 'name', 'site', 'sitename']},
        ...ADDRESS_FIELDS,
        {key: 'phone', label: 'Phone', aliases: ['phone', 'phonenumber', 'telephone']},
    ],
};

const TYPE_LABELS: {[type in CsvImportType]: string} = {
    company: 'Companies',
    contact: 'Contacts',
    location: 'Locations',
};

const GUIDANCE: {[type in CsvImportType]: string} = {
    company: 'Map the ID column to a value that uniquely identifies each company — contacts and locations imported ' +
        'later can then reference their company by that ID (using the same reference name).',
    contact: 'Map the Company ID column to the value the companies were imported with (their ID under the same ' +
        'reference name); a company identifier or exact company name also works.',
    location: 'Map the Company ID column to the value the companies were imported with (their ID under the same ' +
        'reference name); a company identifier or exact company name also works.',
};

const normalizeHeader = (header: string): string => header.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Auto-map fields to headers by name; each header is used at most once, longest alias matches first. */
const autoMap = (fields: IImportField[], headers: string[]): {[field: string]: string} => {
    const mappings: {[field: string]: string} = {};
    const taken = new Set<string>();
    for (const field of fields) {
        for (const alias of field.aliases) {
            const header = headers.find(h => !taken.has(h) && normalizeHeader(h) === alias);
            if (header) {
                mappings[field.key] = header;
                taken.add(header);
                break;
            }
        }
    }
    return mappings;
};

const CELL_BORDER = '1px solid #edebe9';

/** The sticky mapping column: stays put while the row-value preview scrolls horizontally underneath. */
const mappingCellStyle: React.CSSProperties = {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: '#fff',
    borderRight: CELL_BORDER,
    borderBottom: CELL_BORDER,
    padding: '0.5em',
    width: 220,
    minWidth: 220,
    maxWidth: 220,
    textAlign: 'left',
    verticalAlign: 'top'
};

const previewHeaderStyle: React.CSSProperties = {
    borderBottom: CELL_BORDER,
    padding: '0.5em',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    color: '#605e5c'
};

const previewCellStyle: React.CSSProperties = {
    borderBottom: CELL_BORDER,
    padding: '0.5em',
    verticalAlign: 'top'
};

const previewValueStyle: React.CSSProperties = {
    minWidth: 100,
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};

/**
 * The "From CSV" import panel: pick a CSV file (parked in blob storage while mapping), map its columns to
 * record fields, set a reference name, and run the import synchronously with toast feedback. The mapping
 * table is transposed relative to the file — one row per CSV column, previewing the first few data rows —
 * so the user scrolls down to map. Shared by the companies, contacts, and locations import entry points
 * via importType.
 */
const CsvImportPanel: React.FC<ICsvImportPanelProps> = (props) => {
    const [fileId, setFileId] = React.useState<string | undefined>();
    const [fileName, setFileName] = React.useState<string>('');
    const [headers, setHeaders] = React.useState<string[]>([]);
    const [sampleRows, setSampleRows] = React.useState<string[][]>([]);
    const [rowCount, setRowCount] = React.useState<number>(0);
    const [uploading, setUploading] = React.useState(false);
    const [importing, setImporting] = React.useState(false);
    const [referenceName, setReferenceName] = React.useState<string>('');
    const [mappings, setMappings] = React.useState<{[field: string]: string}>({});
    const [result, setResult] = React.useState<ICsvImportResult | undefined>();
    const [uploadFile] = useUploadCsvImportFileMutation();
    const [runImport] = useRunCsvImportMutation();

    const fields = IMPORT_FIELDS[props.importType];
    const typeLabel = TYPE_LABELS[props.importType];

    // Reset transient state each time the panel opens.
    React.useEffect(() => {
        if (props.isOpen) {
            setFileId(undefined);
            setFileName('');
            setHeaders([]);
            setSampleRows([]);
            setRowCount(0);
            setReferenceName('');
            setMappings({});
            setResult(undefined);
        }
    }, [props.isOpen, props.importType]);

    const resolvedToast = {isLoading: false, autoClose: 4000, closeButton: true, closeOnClick: true, draggable: true};

    const onFileSelected = (file: File) => {
        const reader = new FileReader();
        reader.onload = async () => {
            const buffer = reader.result;
            if (!buffer || typeof buffer === 'string') return;
            setUploading(true);
            setResult(undefined);
            const toastId = toast.loading(`Uploading ${file.name}…`, {closeButton: false, closeOnClick: false, draggable: false});
            try {
                const response = await uploadFile({fileName: file.name, file: ArrayBufferToBase64(buffer)}).unwrap();
                if (response.success && response.fileId) {
                    toast.update(toastId, {type: 'success', render: `${file.name} uploaded (${response.rowCount} rows)`, ...resolvedToast});
                    setFileId(response.fileId);
                    setFileName(file.name);
                    setHeaders(response.headers ?? []);
                    setSampleRows(response.sampleRows ?? []);
                    setRowCount(response.rowCount);
                    setMappings(autoMap(fields, response.headers ?? []));
                } else {
                    toast.update(toastId, {type: 'error', render: response.error ?? `Failed to upload ${file.name}`, ...resolvedToast});
                }
            } catch {
                toast.update(toastId, {type: 'error', render: `Failed to upload ${file.name}`, ...resolvedToast});
            } finally {
                setUploading(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    /** The record field a CSV column is currently mapped to, for that column's dropdown selection. */
    const fieldForHeader = (header: string): string => fields.find(f => mappings[f.key] === header)?.key ?? '';

    /** Every field is offered on every column, but a field already claimed by another column is disabled. */
    const optionsForHeader = (header: string): IDropdownOption[] => [
        {key: '', text: '(not mapped)'},
        ...fields.map((field): IDropdownOption => {
            const mappedTo = mappings[field.key];
            const takenElsewhere = !!mappedTo && mappedTo !== header;
            return {
                key: field.key,
                text: `${field.label}${field.required ? ' *' : ''}${takenElsewhere ? ` — mapped to "${mappedTo}"` : ''}`,
                disabled: takenElsewhere
            };
        })
    ];

    const onMappingChange = (header: string, fieldKey: string) => setMappings(current => {
        const next = {...current};
        for (const key of Object.keys(next)) if (next[key] === header) delete next[key];
        if (fieldKey) next[fieldKey] = header;
        return next;
    });

    const requiredMapped = fields.every(f => !f.required || !!mappings[f.key]);
    const missingRequired = fields.filter(f => f.required && !mappings[f.key]);
    // Locations additionally need a name or an address line to identify the record.
    const locationIdentityMapped = props.importType !== 'location' || !!mappings['name'] || !!mappings['address1'];
    const canImport = !!fileId && !importing && requiredMapped && locationIdentityMapped && referenceName.trim().length > 0;

    const onImport = async () => {
        if (!fileId) return;
        setImporting(true);
        setResult(undefined);
        const toastId = toast.loading(`Importing ${typeLabel.toLowerCase()} from ${fileName}…`, {closeButton: false, closeOnClick: false, draggable: false});
        try {
            const runResult = await runImport({
                fileId,
                fileName,
                importType: props.importType,
                referenceName: referenceName.trim(),
                fieldMappings: mappings
            }).unwrap();
            props.onImported?.();
            if (runResult.success) {
                toast.update(toastId, {
                    type: 'success',
                    render: `Imported ${typeLabel.toLowerCase()} from ${fileName}: ${runResult.created} created, ${runResult.updated} updated, ${runResult.skipped} skipped`,
                    ...resolvedToast
                });
                props.onClose();
            } else {
                toast.update(toastId, {
                    type: 'warning',
                    render: `Import of ${fileName} finished with ${runResult.errored} error${runResult.errored === 1 ? '' : 's'}`,
                    ...resolvedToast
                });
                // The parked file is cleaned up after every run — a retry needs a fresh upload.
                setFileId(undefined);
                setResult(runResult);
            }
        } catch {
            toast.update(toastId, {type: 'error', render: `Failed to import ${typeLabel.toLowerCase()} from ${fileName}`, ...resolvedToast});
        } finally {
            setImporting(false);
        }
    };

    const footerButtons = [
        <PrimaryButton
            key='import'
            text={importing ? 'Importing…' : 'Import'}
            iconProps={{iconName: 'Import'}}
            disabled={!canImport}
            onClick={onImport}
        />,
        <DefaultButton key='cancel' text={result ? 'Close' : 'Cancel'} disabled={importing} onClick={props.onClose} />
    ];

    return (
        <PanelWrapper
            isOpen={props.isOpen}
            onDismiss={importing ? undefined : props.onClose}
            title={`Import ${typeLabel} from CSV`}
            type={PanelType.medium}
            isFooterAtBottom={true}
            footerButtons={footerButtons}
        >
            <Stack tokens={{childrenGap: '1em'}}>
                <Text>{GUIDANCE[props.importType]}</Text>
                <input
                    type='file'
                    accept='.csv,text/csv'
                    disabled={uploading || importing}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onFileSelected(file);
                        e.target.value = '';
                    }}
                />
                <RenderIf condition={!!result}>
                    <MessageBar messageBarType={result?.errored ? MessageBarType.warning : MessageBarType.error}>
                        {result && <div>
                            {result.created} created, {result.updated} updated, {result.skipped} skipped, {result.errored} errored.
                            {(result.errors?.length ?? 0) > 0 && <ul style={{margin: '0.5em 0 0', paddingLeft: '1.5em'}}>
                                {result.errors?.map((error, index) => <li key={index}>{error}</li>)}
                            </ul>}
                        </div>}
                    </MessageBar>
                </RenderIf>
                <RenderIf condition={!!fileId}>
                    <Text variant='small'>{fileName} — {rowCount} row{rowCount === 1 ? '' : 's'}</Text>
                    <TextField
                        label='Reference Name'
                        required
                        value={referenceName}
                        placeholder='e.g. legacy-crm'
                        description={'Names this import source. Row IDs are stored under it so re-imports update instead of duplicate' +
                            (props.importType === 'company'
                                ? ', and contact/location imports using the same name can reference these companies.'
                                : ' — use the same name the companies were imported with.')}
                        onChange={(_e, v) => setReferenceName(v ?? '')}
                    />
                    <h3 style={{margin: 0}}>Map Columns</h3>
                    <Text variant='small'>
                        Choose what each CSV column maps to — required fields are marked with *. The first{' '}
                        {sampleRows.length === 1 ? 'row is' : `${sampleRows.length} rows are`} shown as a preview.
                    </Text>
                    <div style={{overflowX: 'auto', border: CELL_BORDER}}>
                        <table style={{borderCollapse: 'separate', borderSpacing: 0, width: '100%'}}>
                            <thead>
                                <tr>
                                    <th style={{...mappingCellStyle, zIndex: 2}}>CSV Column</th>
                                    {sampleRows.map((_row, rowIndex) => (
                                        <th key={rowIndex} style={previewHeaderStyle}>Row {rowIndex + 1}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {headers.map((header, columnIndex) => (
                                    <tr key={columnIndex}>
                                        <th style={mappingCellStyle}>
                                            <div title={header} style={{...previewValueStyle, marginBottom: '0.25em'}}>{header}</div>
                                            <Dropdown
                                                selectedKey={fieldForHeader(header)}
                                                options={optionsForHeader(header)}
                                                onChange={(_e, option) => onMappingChange(header, (option?.key as string) ?? '')}
                                            />
                                        </th>
                                        {sampleRows.map((row, rowIndex) => (
                                            <td key={rowIndex} style={previewCellStyle}>
                                                <div style={previewValueStyle} title={row[columnIndex]}>{row[columnIndex]}</div>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <RenderIf condition={missingRequired.length > 0}>
                        <MessageBar messageBarType={MessageBarType.info}>
                            Map the required field{missingRequired.length === 1 ? '' : 's'}:{' '}
                            {missingRequired.map(f => f.label).join(', ')}.
                        </MessageBar>
                    </RenderIf>
                    <RenderIf condition={requiredMapped && !locationIdentityMapped}>
                        <MessageBar messageBarType={MessageBarType.info}>
                            Map the Location Name or Address 1 field so locations can be identified.
                        </MessageBar>
                    </RenderIf>
                </RenderIf>
            </Stack>
        </PanelWrapper>
    );
};

export default CsvImportPanel;
